// v4.4 — Page Catalogues Commerçants (/kiosque/catalogues).
// Design AYA Kiosque : fond sombre, doré, cartes arrondies, mobile-first.
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, MapPin, Star, ChevronRight, Newspaper } from "lucide-react";
import { trackCatalog } from "@/lib/catalogTracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const FILTERS = [
  { id: "populaires", label: "Populaires" },
  { id: "nouveautes", label: "Nouveaux catalogues" },
  { id: "sponsorises", label: "Sponsorisés" },
];

function CatalogCard({ c, onOpen, idPrefix = "" }) {
  const updated = c.updatedAt ? new Date(c.updatedAt) : null;
  const today = updated && new Date().toDateString() === updated.toDateString();
  return (
    <button
      onClick={() => onOpen(c)}
      data-testid={`catalog-card-${idPrefix}${c.catalogId}`}
      className="text-left rounded-2xl overflow-hidden bg-white/[0.04] border border-white/10 hover:border-amber-400/40 transition-colors flex flex-col"
    >
      <div className="relative aspect-[3/4] bg-white/5">
        <img src={c.coverImage} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        {c.isSponsored && (
          <span className="absolute top-2 left-2 text-[9.5px] font-bold px-2 py-0.5 rounded-full bg-amber-400 text-black">Sponsorisé</span>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col gap-1">
        <div className="text-[13px] font-bold leading-tight">{c.title}</div>
        <div className="text-[11.5px] text-amber-300/90 font-semibold truncate">{c.merchantName}</div>
        <div className="text-[10.5px] text-white/45 flex items-center gap-1">
          <MapPin size={10} /> {c.location} · {c.productCount} produits
        </div>
        <div className="text-[10px] text-white/35">{today ? "Mis à jour aujourd'hui" : `Maj ${updated ? updated.toLocaleDateString("fr-FR") : "—"}`}</div>
        <span className="mt-auto pt-1 text-[11.5px] font-semibold text-amber-300 flex items-center">Voir le catalogue <ChevronRight size={13} /></span>
      </div>
    </button>
  );
}

export default function KiosqueCatalogsPage({ merchantMode = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("populaires");
  const [category, setCategory] = useState("");
  const [data, setData] = useState({ catalogs: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const merchant = merchantMode ? window.location.pathname.split("/").pop() : "";
  const src = searchParams.get("src") || "direct";

  useEffect(() => {
    trackCatalog("merchant_catalog_view", { src });
    if (["instagram", "tiktok", "facebook", "whatsapp"].includes(src)) trackCatalog("social_to_catalog_click", { src });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (filter) params.set("filter", filter);
    if (category) params.set("category", category);
    if (merchant) params.set("merchant", merchant);
    const t = setTimeout(() => {
      axios.get(`${BACKEND_URL}/api/v3/catalogs?${params}`)
        .then((r) => setData(r.data || {}))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, filter, category, merchant]);

  const catalogs = data.catalogs || [];
  const sponsored = useMemo(() => catalogs.filter((c) => c.isSponsored).slice(0, 4), [catalogs]);

  const openCatalog = (c) => {
    trackCatalog("catalog_open", { catalog_id: c.catalogId, merchant_id: c.merchantId, src });
    navigate(`/kiosque/catalogue/${c.catalogId}?src=${src}`);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0d] text-white pb-16" data-testid="kiosque-catalogs-page">
      <div className="sticky top-0 z-10 bg-[#0a0a0d]/95 backdrop-blur border-b border-white/8">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/kiosque")} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/10 text-white/80" data-testid="catalogs-back-btn">
              <ArrowLeft size={19} />
            </button>
            <div>
              <div className="text-[15px] font-bold">🏪 Catalogues Commerçants</div>
              <div className="text-[10.5px] text-white/45">Catalogues, offres et produits des commerçants présents sur AYA</div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 bg-white/[0.06] border border-white/10 rounded-xl px-3 py-2">
            <Search size={15} className="text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un commerçant, un catalogue…"
              className="bg-transparent outline-none text-[13px] flex-1 placeholder:text-white/30"
              data-testid="catalogs-search-input"
            />
          </div>
          <div className="mt-2.5 flex gap-2 overflow-x-auto no-scrollbar pb-0.5">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`catalogs-filter-${f.id}`}
                className={`shrink-0 text-[11.5px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${filter === f.id ? "bg-amber-400 text-black border-amber-400" : "border-white/15 text-white/65 hover:border-white/35"}`}
              >
                {f.label}
              </button>
            ))}
            {(data.categories || []).map((c) => (
              <button
                key={c}
                onClick={() => setCategory(category === c ? "" : c)}
                className={`shrink-0 text-[11.5px] px-3 py-1.5 rounded-full border capitalize transition-colors ${category === c ? "bg-white/15 border-white/40" : "border-white/12 text-white/55 hover:border-white/30"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-6">
        {sponsored.length > 0 && filter === "populaires" && !q && !category && (
          <div>
            <div className="text-[12px] font-bold text-amber-300/90 uppercase tracking-wider mb-2.5">✨ Catalogues sponsorisés</div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4">
              {sponsored.map((c) => (
                <div key={c.catalogId} className="w-[150px] shrink-0"><CatalogCard c={c} onOpen={openCatalog} idPrefix="sponsored-" /></div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4.6] rounded-2xl bg-white/[0.05] animate-pulse" />)}
          </div>
        ) : catalogs.length === 0 ? (
          <div className="text-center text-white/40 text-sm py-14">Aucun catalogue trouvé.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {catalogs.map((c) => <CatalogCard key={c.catalogId} c={c} onOpen={openCatalog} />)}
          </div>
        )}

        {/* Boucle inverse : commerce → contenu */}
        <button
          onClick={() => { trackCatalog("catalog_to_kiosque", { src: "catalogs_page" }); navigate("/kiosque"); }}
          data-testid="catalogs-to-kiosque-btn"
          className="w-full rounded-2xl border border-white/12 bg-white/[0.04] hover:border-white/30 px-4 py-4 text-left flex items-center gap-3 transition-colors"
        >
          <div className="w-10 h-10 rounded-xl bg-white/[0.07] flex items-center justify-center shrink-0"><Newspaper size={18} className="text-white/70" /></div>
          <div className="flex-1">
            <div className="text-[13px] font-bold">Explorez aussi le Kiosque AYA</div>
            <div className="text-[11px] text-white/50">Journaux, livres, mangas et revues</div>
          </div>
          <ChevronRight size={16} className="text-white/40" />
        </button>
      </div>
    </div>
  );
}
